/**
 * @file News.js
 * @module src/js/components/News
 * @description
 * **News component for displaying and managing news entries.**
 * - Fetches news data from the backend and displays it in a paginated table.
 * - Allows authenticated users to upload, edit, and delete news entries.
 * - Includes a search bar for filtering news based on date and title.
 * - Highlights recent news (news fewer than 3 months old) with a "New" indicator.
 *
 * @version
 * <p>
 *  2025-11-26: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: The "NEW" image added for news within 90 days. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Add modal messages. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteEntry. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../contexts/UserProvider";
import { useTheme } from "../contexts/ThemeProvider";
import NumberPagination from './NumberPagination';
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import useDeleteEntry from "../hooks/useDeleteEntry";
import PagedTable from './PagedTable';
import UploadForm from './UploadForm';
import SearchBar from "./SearchBar";
import SpanARow from "./SpanARow";
import { ExtendedMarkdown } from "./UtilityComponents";
import newGif from '../../images/new.gif';

export default function News() {
    const { uid } = useUser();
    const { theme } = useTheme();
    const fetchFromBackend = useFetchFromBackend();
    const [news, setNews] = useState([]);
    const [filteredNews, setFilteredNews] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [updatedID, setUpdatedID] = useState(-1);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const deleteEntry = useDeleteEntry({ setData: setNews, setDeleting });

    const numPageButtons = 10;
    const numItemsPerPage = 15;

    const newsOnCurrentPage = useMemo(() => {
        if (!filteredNews || filteredNews.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredNews.slice(start, end);
    }, [filteredNews, currentPage]);

    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: "/api/news" });
            setNews(data || []);
            setLoading(false);
        }
        fetchData();
    }, [fetchFromBackend]);

    const pagination = (
        <NumberPagination
            length={filteredNews.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            headerRef={headerRef}
        />
    );

    // The useMemo hook is used to memoize the filterKeys array.
    // The array will only be created once and will not be recreated on every render.
    // This is important because the SearchBar component will use this array as a dependency.
    // If the array were recreated on every render, it would cause the SearchBar and News to re-render indefinitely.
    // This would lead to an infinite loop and stuck UI.
    const filterKeys = useMemo(() => ['date', 'title'], []);
    const searchBar = (
        <SearchBar
            data={news}
            setFilteredData={setFilteredNews}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            filterKeys={filterKeys}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className='hori-vert-center' width='15%'>Date</th>
                        <th className='hori-vert-center' colSpan={uid !== 'Guest' ? 2 : 1}>Title</th>
                    </tr>
                </thead>
                <tbody>
                    <UploadForm
                        request="/api/news"
                        setData={setNews}
                        text="📰 Upload news"
                        textarea={true}
                        from="/"
                        scrollTo="news"
                    />
                    {filteredNews.length === 0 && (
                        <SpanARow
                            colSpan={uid === 'Guest' ? 2 : 3}
                            text="No news found"
                            loading={loading}
                        />
                    )}
                    {newsOnCurrentPage.map((item, i) => (
                        item.ID !== updatedID ? (
                            <tr key={i}>
                                <td className='hori-vert-center'>
                                    {item.date}<br />
                                    {new Date(item.date) >= new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) && (
                                        <img src={newGif} alt='New' style={{ marginLeft: '5px' }} />
                                    )}
                                </td>
                                <td style={{ textAlign: 'left', verticalAlign: 'middle' }}>
                                    <ExtendedMarkdown>
                                        {item.title}
                                    </ExtendedMarkdown>
                                </td>
                                {uid !== 'Guest' && (
                                    <td className='hori-vert-center' width='10%'>
                                        <button
                                            onClick={() => setUpdatedID(item.ID)}
                                            disabled={deleting}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteEntry({
                                                request: "/api/news",
                                                id: item.ID,
                                                from: '/',
                                                scrollTo: 'news',
                                            })}
                                            disabled={deleting}
                                            style={{
                                                color: theme === 'dark' ? 'lightcoral' : 'red',
                                                outline: `2px solid ${theme === 'dark' ? 'lightcoral' : 'rgba(255, 0, 0, 0.1)'}`,
                                                outlineOffset: '-2px',
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ) : (
                            <UploadForm
                                key={i}
                                request="/api/news"
                                setData={setNews}
                                textarea={true}
                                item={item}
                                updatedID={updatedID}
                                setUpdatedID={setUpdatedID}
                                from="/"
                                scrollTo="news"
                            />
                        )
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <PagedTable
            header="News"
            headerRef={headerRef}
            numberPagination={filteredNews.length > numItemsPerPage ? pagination : null}
            searchBar={searchBar}
            table={table}
        />
    );
}
